import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Users, Clock } from 'lucide-react';
import apiService from '../services/api';
import toast from 'react-hot-toast';

interface Employee {
  id: number;
  type: string;
  rate: number;
  shiftARate?: number;
  shiftBRate?: number;
  user: {
    id: number;
    name: string;
    email: string;
  };
}

interface MachineGroup {
  id: number;
  name: string;
  description?: string;
  shiftAEmployeeId?: number;
  shiftBEmployeeId?: number;
  shiftAEmployee?: Employee;
  shiftBEmployee?: Employee;
}

interface ShiftAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  group: MachineGroup | null;
  employees: Employee[];
}

interface ShiftAssignmentForm {
  shiftAEmployeeId: number | string;
  shiftBEmployeeId: number | string;
}

const ShiftAssignmentModal: React.FC<ShiftAssignmentModalProps> = ({
  isOpen,
  onClose,
  group,
  employees
}) => {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, reset, setValue, watch } = useForm<ShiftAssignmentForm>({
    defaultValues: {
      shiftAEmployeeId: group?.shiftAEmployeeId || "",
      shiftBEmployeeId: group?.shiftBEmployeeId || "",
    }
  });

  const shiftAEmployeeId = watch('shiftAEmployeeId');
  const shiftBEmployeeId = watch('shiftBEmployeeId');

  const assignShiftEmployeesMutation = useMutation({
    mutationFn: (data: { shiftAEmployeeId: number | null; shiftBEmployeeId: number | null }) =>
      apiService.put(`/machine-groups/${group?.id}/assign-shift-employees`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machines'] });
      toast.success('Shift employees assigned successfully');
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to assign shift employees');
    },
  });

  const onSubmit = async (data: ShiftAssignmentForm) => {
    if (!group) return;
    
    // Convert empty strings to null for the API
    const submitData = {
      shiftAEmployeeId: data.shiftAEmployeeId === "" ? null : Number(data.shiftAEmployeeId),
      shiftBEmployeeId: data.shiftBEmployeeId === "" ? null : Number(data.shiftBEmployeeId),
    };
    
    setIsSubmitting(true);
    try {
      await assignShiftEmployeesMutation.mutateAsync(submitData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!isOpen || !group) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Assign Shift Employees</h2>
              <p className="text-sm text-gray-600">Group: {group.name}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Shift A */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <span className="text-2xl">☀️</span>
                <div>
                  <h3 className="font-semibold text-gray-900">Shift A (8 AM - 8 PM)</h3>
                  <p className="text-sm text-gray-600">Morning shift</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Employee
                </label>
                <select
                  {...register('shiftAEmployeeId', { valueAsNumber: true })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">No employee assigned</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.user.name} - {employee.type} (Rate: {employee.rate})
                    </option>
                  ))}
                </select>
              </div>

              {shiftAEmployeeId && shiftAEmployeeId !== "" && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center space-x-2 text-sm">
                    <Clock className="h-4 w-4 text-yellow-600" />
                    <span className="font-medium text-yellow-800">Shift A Rate</span>
                  </div>
                  <p className="text-sm text-yellow-700 mt-1">
                    {employees.find(e => e.id === Number(shiftAEmployeeId))?.shiftARate 
                      ? `Custom rate: ${employees.find(e => e.id === Number(shiftAEmployeeId))?.shiftARate}`
                      : `Default rate: ${employees.find(e => e.id === Number(shiftAEmployeeId))?.rate}`
                    }
                  </p>
                </div>
              )}
            </div>

            {/* Shift B */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <span className="text-2xl">🌙</span>
                <div>
                  <h3 className="font-semibold text-gray-900">Shift B (8 PM - 8 AM)</h3>
                  <p className="text-sm text-gray-600">Night shift</p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Employee
                </label>
                <select
                  {...register('shiftBEmployeeId', { valueAsNumber: true })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">No employee assigned</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.user.name} - {employee.type} (Rate: {employee.rate})
                    </option>
                  ))}
                </select>
              </div>

              {shiftBEmployeeId && shiftBEmployeeId !== "" && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center space-x-2 text-sm">
                    <Clock className="h-4 w-4 text-blue-600" />
                    <span className="font-medium text-blue-800">Shift B Rate</span>
                  </div>
                  <p className="text-sm text-blue-700 mt-1">
                    {employees.find(e => e.id === Number(shiftBEmployeeId))?.shiftBRate 
                      ? `Custom rate: ${employees.find(e => e.id === Number(shiftBEmployeeId))?.shiftBRate}`
                      : `Default rate: ${employees.find(e => e.id === Number(shiftBEmployeeId))?.rate}`
                    }
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Assigning...' : 'Assign Employees'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ShiftAssignmentModal;
